import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Copy,
  Check,
  Clock,
  Building2,
  Receipt,
  CloudUpload,
  CheckCircle2,
  Ban,
  AlertTriangle,
  RotateCw,
  Store,
  FileText,
  Image as ImageIcon,
  ChevronDown,
  Star,
  Camera,
  Send,
  Timer,
  Lock,
  ShieldAlert,
  LogIn,
} from 'lucide-react';
import { useUIStore } from '../../stores/useUIStore';
import { OrderDetail, OrderStatus, orderApi, OrderApiError, getStoredAuthToken } from '../../services/orderApi';
import { cn } from '../../utils/cn';
import { compressImage } from '../../utils/imageCompression';

export interface OrderPageProps {
  orderCode?: string;
}

export const OrderPage: React.FC<OrderPageProps> = ({ orderCode }) => {
  const navigateTo = useUIStore((s) => s.navigateTo);
  const activeOrderCode = useUIStore((s) => s.activeOrderCode);
  const openAuthModal = useUIStore((s) => s.openAuthModal);

  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);

  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Cancel order state
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Toast
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Countdown timer state
  const [secondsRemaining, setSecondsRemaining] = useState<number>(3600);
  const [isExpiredByTimer, setIsExpiredByTimer] = useState(false);

  // Collapsible transfer details state (collapsed by default unless PENDIENTE_PAGO)
  const [isTransferOpen, setIsTransferOpen] = useState(false);

  // Review states
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewFile, setReviewFile] = useState<File | null>(null);
  const [reviewPreview, setReviewPreview] = useState<string | null>(null);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewCheckData, setReviewCheckData] = useState<{
    can_review: boolean;
    already_reviewed: boolean;
    requires_login?: boolean;
    reason?: string;
    is_first_review?: boolean;
    reward_discount_pct?: number;
  } | null>(null);
  const [reviewRewardInfo, setReviewRewardInfo] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const effectiveCode = useMemo(() => {
    // 1. Explicit prop passed to component
    if (orderCode) return orderCode.replace('#', '').trim().toUpperCase();

    // 2. Active order code from store (updated in real-time by navigateTo and CartPage)
    if (activeOrderCode) return activeOrderCode.replace('#', '').trim().toUpperCase();

    // 3. Current URL search params or path
    if (typeof window !== 'undefined' && window.location) {
      const params = new URLSearchParams(window.location.search);
      const qCode = params.get('code');
      if (qCode) return qCode.replace('#', '').trim().toUpperCase();
      const pathParts = window.location.pathname.split('/');
      const lastPart = pathParts[pathParts.length - 1];
      if (lastPart && (lastPart.startsWith('FN-') || lastPart.startsWith('fn-'))) {
        return lastPart.toUpperCase();
      }
    }

    // 4. Fallback from local storage
    try {
      const last = localStorage.getItem('fn_last_order');
      if (last) {
        const parsed = JSON.parse(last);
        if (parsed.order_code) return parsed.order_code.replace('#', '').trim().toUpperCase();
      }
    } catch {}
    return null;
  }, [orderCode, activeOrderCode]);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  const copyToClipboard = (text: string, _label?: string) => {
    if (!text) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        showToast(`Copiado: ${text}`);
      }).catch(() => {
        showToast(`Copiado: ${text}`);
      });
    } else {
      showToast(`Copiado: ${text}`);
    }
  };

  // Fetch Order
  const fetchOrderDetails = useCallback(async (code: string) => {
    try {
      setIsLoading(true);
      setErrorMsg(null);
      setErrorStatus(null);
      const data = await orderApi.getOrder(code);
      setOrder(data);
      if (data.remaining_seconds && data.remaining_seconds > 0) {
        setSecondsRemaining(data.remaining_seconds);
      } else if (data.expires_at) {
        const diff = Math.max(0, Math.floor((new Date(data.expires_at).getTime() - Date.now()) / 1000));
        setSecondsRemaining(diff);
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Pedido no encontrado');
      setErrorStatus(err.status || (err instanceof OrderApiError ? err.status : null));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Reset states when effectiveCode changes to prevent displaying stale data from a previous order
    setOrder(null);
    setErrorMsg(null);
    setErrorStatus(null);
    setReviewCheckData(null);
    setSelectedFile(null);
    setFilePreview(null);
    setReviewFile(null);
    setReviewPreview(null);
    setReviewSubmitted(false);
    setReviewRewardInfo(null);
    setReviewError(null);

    if (effectiveCode) {
      fetchOrderDetails(effectiveCode);
    } else {
      setIsLoading(false);
      setErrorMsg('No se especificó ningún código de pedido.');
    }
  }, [effectiveCode, fetchOrderDetails]);

  // Polling for automated delivery or status updates
  useEffect(() => {
    if (!order) return;
    if (order.status === 'CANCELADO' || order.status === 'COMPLETADO' || order.status === 'EXPIRADO') return;

    const intervalTime = order.payment_method === 'SALDO_BILLETERA' ? 2000 : 8000;
    const poller = setInterval(async () => {
      if (!effectiveCode) return;
      try {
        const refreshed = await orderApi.getOrder(effectiveCode);
        if (refreshed.status !== order.status) {
          setOrder(refreshed);
          if (refreshed.status === 'COMPLETADO') {
            setTimeout(() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 150);
            const isRefreshedRecharge =
              (refreshed.order_code || '').replace('#', '').trim().toUpperCase().startsWith('FN-REC') ||
              (refreshed.order_code || '').replace('#', '').trim().toUpperCase().startsWith('REC-') ||
              (refreshed.items || []).some((it: any) => {
                const oid = String(it.offerId || it.offer_id || '').toLowerCase();
                const name = String(it.name || '').toLowerCase();
                return oid === 'wallet_recharge' || name.includes('recarga de saldo') || name.includes('g-coins');
              });
            showToast(
              isRefreshedRecharge
                ? '¡Saldo acreditado con éxito en tu cuenta!'
                : '¡Regalos entregados con éxito a tu cuenta de Fortnite!'
            );
          }
        }
      } catch {}
    }, intervalTime);

    return () => clearInterval(poller);
  }, [order, effectiveCode]);

  // Sync collapsible transfer state with order status
  useEffect(() => {
    if (order) {
      if (order.status === 'PENDIENTE_PAGO') {
        setIsTransferOpen(true);
      } else {
        setIsTransferOpen(false);
      }
    }
  }, [order?.status]);

  // Check review status when order is COMPLETADO
  useEffect(() => {
    if (order && order.status === 'COMPLETADO') {
      const code = order.order_code.replace('#', '').trim();
      const urlToken =
        typeof window !== 'undefined' && window.location
          ? new URLSearchParams(window.location.search).get('token')
          : '';
      const authToken = getStoredAuthToken();
      const headers: Record<string, string> = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

      const checkUrl = urlToken
        ? `/api/reviews/check-order/${code}?token=${encodeURIComponent(urlToken)}`
        : `/api/reviews/check-order/${code}`;

      fetch(checkUrl, { headers })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data) {
            setReviewCheckData(data);
            if (data.already_reviewed) {
              setReviewSubmitted(true);
            }
          }
        })
        .catch(() => {});
    }
  }, [order?.status, order?.order_code]);

  // Timer Tick
  useEffect(() => {
    if (!order || order.status !== 'PENDIENTE_PAGO') return;
    if (secondsRemaining <= 0) return;

    const ticker = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(ticker);
          if (order) {
            setOrder({ ...order, status: 'CANCELADO' });
            setIsExpiredByTimer(true);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(ticker);
  }, [order, secondsRemaining]);

  const formattedTimer = useMemo(() => {
    const mins = Math.floor(secondsRemaining / 60);
    const secs = secondsRemaining % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }, [secondsRemaining]);

  // File Handlers
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert('El archivo no debe exceder los 10 MB.');
      return;
    }

    const processed = await compressImage(file);
    setSelectedFile(processed);
    if (processed.type.startsWith('image/')) {
      const url = URL.createObjectURL(processed);
      setFilePreview(url);
    } else {
      setFilePreview(null);
    }
  };

  const handleUploadReceipt = async () => {
    if (!selectedFile || !order) return;
    setIsUploading(true);
    try {
      const res = await orderApi.uploadReceipt(order.order_code, selectedFile);
      setOrder((prev) =>
        prev
          ? {
              ...prev,
              status: 'PAGO_INFORMADO',
              receipt_url: res.receipt_url || prev.receipt_url,
            }
          : null
      );
      showToast('¡Comprobante subido y notificado a Lucas!');
    } catch (err: any) {
      alert(`Error al subir comprobante: ${err.message || 'Intenta nuevamente.'}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!order) return;
    setIsCancelling(true);
    try {
      await orderApi.cancelOrder(order.order_code);
      setOrder((prev) => (prev ? { ...prev, status: 'CANCELADO', cancelled_by: 'USER' } : null));
      setShowCancelConfirm(false);
      showToast('Pedido cancelado correctamente.');
    } catch (err: any) {
      alert(`Error al cancelar: ${err.message || 'No se pudo cancelar el pedido.'}`);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!order) return;
    setIsSubmittingReview(true);
    setReviewError(null);

    try {
      const code = order.order_code.replace('#', '').trim();
      const urlToken =
        typeof window !== 'undefined' && window.location
          ? new URLSearchParams(window.location.search).get('token')
          : '';
      const authToken = getStoredAuthToken();

      const formData = new FormData();
      formData.append('order_code', code);
      formData.append('rating', String(reviewRating));
      if (reviewComment.trim()) {
        formData.append('comment', reviewComment.trim());
      }
      if (reviewFile) {
        formData.append('file', reviewFile);
      }
      if (urlToken) {
        formData.append('token', urlToken);
      }

      const headers: Record<string, string> = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

      const res = await fetch('/api/reviews', {
        method: 'POST',
        headers,
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Error al enviar la reseña.');
      }

      setReviewSubmitted(true);
      if (data.coupon_code) {
        const pct = data.discount_pct || reviewCheckData?.reward_discount_pct || 10;
        setReviewRewardInfo(
          `¡Cupón generado: ${data.coupon_code} (${pct}% OFF)! Ya está disponible en tu cuenta.`
        );
      }
      showToast('¡Gracias por tu reseña! Cupón asignado.');
    } catch (err: any) {
      setReviewError(err.message || 'Error al enviar reseña.');
    } finally {
      setIsSubmittingReview(false);
    }
  };

  // Check if order is a balance recharge (G-Coins / Saldo de billetera)
  const isWalletRecharge = useMemo(() => {
    if (!order) return false;
    const code = (order.order_code || '').replace('#', '').trim().toUpperCase();
    if (code.startsWith('FN-REC') || code.startsWith('REC-') || code.startsWith('REC_')) {
      return true;
    }
    const items = order.items || [];
    if (items.length > 0) {
      return items.some((it: any) => {
        const oid = String(it.offerId || it.offer_id || '').toLowerCase();
        if (oid === 'wallet_recharge') return true;
        const name = String(it.name || '').toLowerCase();
        return name.includes('recarga de saldo') || name.includes('g-coins');
      });
    }
    if (typeof (order as any).cart_summary === 'string') {
      const summary = String((order as any).cart_summary).toLowerCase();
      if (summary.includes('recarga de saldo') || summary.includes('g-coins')) return true;
    }
    return false;
  }, [order]);

  // Check if order consists solely of direct game recharges (e.g. 12500 Pavos or Club de Fortnite)
  const isDirectRecharge = useMemo(() => {
    if (!order || isWalletRecharge) return false;
    const items = order.items || [];
    if (items.length > 0) {
      return items.every((it: any) => {
        const oid = String(it.offerId || it.offer_id || '').toLowerCase();
        const name = String(it.name || '').toLowerCase();
        return (
          oid.startsWith('recharge_') ||
          oid === 'recharge_vbucks_12500' ||
          oid === 'recharge_crew' ||
          oid === 'crew' ||
          it.is_recharge ||
          it.category === 'Recargas' ||
          name.includes('recarga') ||
          name.includes('club de fortnite')
        );
      });
    }
    return false;
  }, [order, isWalletRecharge]);

  // Dynamic Title for Card 4: Items in order
  const orderItemsTitle = useMemo(() => {
    if (isWalletRecharge) {
      return 'RECARGA DE SALDO EN ESTE PEDIDO';
    }
    if (isDirectRecharge) {
      return 'RECARGA EN ESTE PEDIDO';
    }
    const hasRecharge = (order?.items || []).some((it: any) => {
      const oid = String(it.offerId || it.offer_id || '').toLowerCase();
      return oid.startsWith('recharge_') || oid === 'crew' || it.is_recharge || it.category === 'Recargas';
    });
    if (hasRecharge) {
      return 'PRODUCTOS EN ESTE PEDIDO';
    }
    return 'COSMÉTICOS EN ESTE PEDIDO';
  }, [isWalletRecharge, isDirectRecharge, order?.items]);

  // Validation for post-delivery review submission
  const isReviewValid = useMemo(() => {
    if (!reviewRating || reviewRating < 1) return false;
    if (!reviewComment.trim()) return false;
    if (!isWalletRecharge && !reviewFile) return false;
    return true;
  }, [reviewRating, reviewComment, isWalletRecharge, reviewFile]);

  // Status Badge Mapper (1:1 with Images 1 & 3)
  const renderStatusBadge = (status: OrderStatus) => {
    switch (status) {
      case 'PENDIENTE_PAGO':
        return (
          <div
            className="w-fit px-3 py-1 bg-[#fef9c3] border border-[#fde68a] text-[#b45309] font-heading font-black text-xs uppercase tracking-wider flex items-center gap-1.5"
            style={{
              clipPath:
                'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
            }}
          >
            <Clock className="w-3.5 h-3.5 stroke-[2.5]" />
            <span>ESPERANDO PAGO</span>
          </div>
        );
      case 'CANCELADO':
        return (
          <div
            className="w-full px-3 py-1 bg-[#fef2f2] border border-[#fecaca] text-[#dc2626] font-heading font-black text-xs uppercase tracking-wider flex items-center gap-1.5"
            style={{
              clipPath:
                'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
            }}
          >
            <Ban className="w-3.5 h-3.5 stroke-[2.5]" />
            <span>PEDIDO CANCELADO</span>
          </div>
        );
      case 'PAGO_INFORMADO':
        return (
          <div
            className="w-fit px-3 py-1 bg-[#dbeafe] border border-[#bfdbfe] text-[#1447e6] font-heading font-black text-xs uppercase tracking-wider flex items-center gap-1.5"
            style={{
              clipPath:
                'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
            }}
          >
            <RotateCw className="w-3.5 h-3.5 animate-spin" />
            <span>COMPROBANTE EN REVISIÓN</span>
          </div>
        );
      case 'APROBADO_ENVIANDO':
        return (
          <div
            className="w-fit px-3 py-1 bg-[#e0e7ff] border border-[#c7d2fe] text-[#1447e6] font-heading font-black text-xs uppercase tracking-wider flex items-center gap-1.5"
            style={{
              clipPath:
                'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
            }}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>
              {isWalletRecharge
                ? 'ACREDITANDO SALDO...'
                : isDirectRecharge
                ? 'CARGANDO RECARGA...'
                : 'ENVIANDO REGALOS...'}
            </span>
          </div>
        );
      case 'COMPLETADO':
        return (
          <div
            className="w-fit px-3 py-1 bg-[#dcfce7] border border-[#86efac] text-[#15803d] font-heading font-black text-xs uppercase tracking-wider flex items-center gap-1.5"
            style={{
              clipPath:
                'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
            }}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>{isWalletRecharge ? 'SALDO ACREDITADO' : 'ENTREGADO CON ÉXITO'}</span>
          </div>
        );
      case 'EXPIRADO':
        return (
          <div
            className="w-fit px-3 py-1 bg-[#f1f5f9] border border-[#cbd5e1] text-[#475569] font-heading font-black text-xs uppercase tracking-wider flex items-center gap-1.5"
            style={{
              clipPath:
                'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
            }}
          >
            <Clock className="w-3.5 h-3.5" />
            <span>EXPIRADO</span>
          </div>
        );
      default:
        return null;
    }
  };

  // Static Bank details resolution
  const bankDetails = useMemo(() => {
    if (order?.bank_details) return order.bank_details;
    if (order?.currency === 'MXN') {
      return {
        bank: 'Finco Pay',
        holder: 'Lucas Tirimacco',
        clabe: '734180020023721778',
        alias: null,
      };
    }
    return {
      bank: 'Mercado Pago',
      holder: 'Lucas Tirimacco',
      alias: 'ltirimacco.mp',
      clabe: null,
    };
  }, [order]);

  // Formatted price string with comma support (e.g. $10.498,6 ARS)
  const formattedPriceString = useMemo(() => {
    if (!order) return '$0 ARS';
    const amount = Number(order.total_price);
    const parts = amount.toFixed(1).split('.');
    const integerPart = Number(parts[0]).toLocaleString('es-AR');
    const decimalPart = parts[1] && parts[1] !== '0' ? `,${parts[1]}` : '';
    return `$${integerPart}${decimalPart} ${order.currency}`;
  }, [order]);

  return (
    <div className="w-full min-h-screen bg-transparent text-[#06173f] py-8 sm:py-12 px-4 sm:px-6">
      {/* Toast Notification */}
      {toastMessage && (
        <div
          className="fixed bottom-6 right-6 z-50 bg-[#06173f] text-white px-4 py-2.5 shadow-2xl flex items-center gap-2 text-xs font-bold font-heading border border-[#1447e6]"
          style={{ clipPath: 'var(--cut)' }}
        >
          <Check className="w-4 h-4 text-[#5ccbff]" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Main Container - Unified margins matching Cart, Profile, Recargas, and Sorteos */}
      <main className="max-w-[1200px] mx-auto space-y-6 sm:space-y-8">
        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-24 space-y-4">
            <div className="w-12 h-12 border-4 border-[#1447e6] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-bold text-[var(--soft)] uppercase tracking-wider font-heading">
              Cargando detalles de tu pedido...
            </p>
          </div>
        )}

        {/* Error State */}
        {!isLoading && errorMsg && (
          <div
            className="pn bg-white border border-[var(--line)] p-8 text-center space-y-4 shadow-sm"
            style={{ clipPath: 'var(--cut)' }}
          >
            {errorStatus === 401 ? (
              <>
                <div className="w-16 h-16 bg-blue-100 text-[#1447e6] rounded-full flex items-center justify-center text-2xl mx-auto shadow-inner">
                  <Lock className="w-8 h-8 stroke-[2.5]" />
                </div>
                <div className="space-y-1">
                  <h2 className="text-2xl font-black text-[#06173f] font-heading uppercase">
                    Inicia sesión para ver este pedido
                  </h2>
                  <p className="text-sm text-[var(--soft)] max-w-md mx-auto">
                    {errorMsg || 'Este pedido pertenece a una cuenta de usuario registrado. Inicia sesión con la cuenta correspondiente para ver los detalles.'}
                  </p>
                </div>
                <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => openAuthModal('login')}
                    className="btn font-bold cursor-pointer transition flex items-center justify-center gap-2 py-2.5 px-6 text-sm font-heading"
                    style={{ backgroundColor: '#1447e6', color: '#ffffff' }}
                  >
                    <LogIn className="w-4 h-4" />
                    <span>Iniciar Sesión</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigateTo('shop', '/tienda')}
                    className="btn lt cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <Store className="w-4 h-4" />
                    <span>Volver a la Tienda</span>
                  </button>
                </div>
              </>
            ) : errorStatus === 403 ? (
              <>
                <div className="w-16 h-16 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-2xl mx-auto shadow-inner">
                  <ShieldAlert className="w-8 h-8 stroke-[2.5]" />
                </div>
                <div className="space-y-1">
                  <h2 className="text-2xl font-black text-amber-950 font-heading uppercase">
                    Este pedido no te pertenece
                  </h2>
                  <p className="text-sm text-[var(--soft)] max-w-md mx-auto">
                    {errorMsg || 'Este pedido pertenece a otra cuenta de cliente. Para proteger la privacidad del comprador, inicia sesión con la cuenta a la que pertenece este pedido o utiliza el enlace firmado que te enviamos por correo o WhatsApp.'}
                  </p>
                </div>
                <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => openAuthModal('login')}
                    className="btn font-bold cursor-pointer transition flex items-center justify-center gap-2 py-2.5 px-6 text-sm font-heading"
                    style={{ backgroundColor: '#1447e6', color: '#ffffff' }}
                  >
                    <LogIn className="w-4 h-4" />
                    <span>Cambiar de cuenta</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigateTo('shop', '/tienda')}
                    className="btn lt cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <Store className="w-4 h-4" />
                    <span>Volver a la Tienda</span>
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-2xl mx-auto">
                  <AlertTriangle className="w-8 h-8" />
                </div>
                <div className="space-y-1">
                  <h2 className="text-2xl font-black text-red-700 font-heading">
                    Pedido no encontrado
                  </h2>
                  <p className="text-sm text-[var(--soft)] max-w-md mx-auto">
                    {errorMsg}
                  </p>
                </div>
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => navigateTo('shop', '/tienda')}
                    className="btn lt cursor-pointer flex items-center justify-center gap-1.5 mx-auto"
                  >
                    <Store className="w-4 h-4 mr-1.5" />
                    <span>Volver a la Tienda</span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Order Details View (Exact Match with Images 1, 2, and 3) */}
        {!isLoading && order && (
          <div className="space-y-5">
            {/* TARJETA RESEÑA POST-COMPRA (Arriba de todo cuando COMPLETADO) */}
            {order.status === 'COMPLETADO' && (
              <div
                id="order-review-card"
                className="pn bg-white border-2 border-[var(--line)] p-3.5 sm:p-4 space-y-2.5 shadow-sm"
                style={{
                  clipPath: 'polygon(10px 0, 100% 0, 100% 100%, 0 100%, 0 10px)',
                  filter: 'drop-shadow(4px 4px 0 rgba(20, 71, 230, 0.12))',
                }}
              >
                <div className="flex items-center justify-between border-b border-[var(--line)] pb-2">
                  <div className="flex items-center gap-1.5">
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500 shrink-0" />
                    <h3 className="font-bold text-xs sm:text-sm text-[#06173f] font-heading uppercase tracking-tight">
                      ¿Qué te pareció el servicio?
                    </h3>
                  </div>
                  <span
                    className="px-2 py-0.5 text-[10px] sm:text-[11px] font-bold text-emerald-800 bg-[#ecfdf5] border border-[#a7f3d0] flex items-center gap-1 font-heading shrink-0"
                    style={{ clipPath: 'var(--cut-sm)' }}
                    title="¡Califica tu compra y obtén 10% OFF!"
                  >
                    <Star className="w-3 h-3 text-emerald-600 fill-emerald-600" />
                    <span>10% OFF</span>
                  </span>
                </div>

                {reviewSubmitted ? (
                  <div className="p-2.5 bg-emerald-50 border border-emerald-300 rounded text-xs text-emerald-900 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <div>
                      <p className="font-bold font-heading uppercase text-xs text-emerald-950">
                        ¡Reseña enviada con éxito!
                      </p>
                      <p className="text-[11px] text-emerald-800 mt-0.5">
                        {reviewRewardInfo ||
                          '¡Muchas gracias! Tu cupón del 10% OFF fue acreditado en tu cuenta.'}
                      </p>
                    </div>
                  </div>
                ) : reviewCheckData?.requires_login ? (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center space-y-2.5">
                    <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto">
                      <Lock className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-black text-xs sm:text-sm text-blue-950 font-heading uppercase">
                        Inicia sesión para opinar
                      </h4>
                      <p className="text-xs text-blue-800 max-w-sm mx-auto mt-0.5 leading-snug">
                        Para verificar la autoría de tu compra y acreditar tu cupón del 10% OFF en tu cuenta, inicia sesión con la cuenta de este pedido.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => openAuthModal('login')}
                      className="btn sm font-bold cursor-pointer transition inline-flex items-center justify-center gap-1.5 py-2 px-4 text-xs font-heading"
                      style={{ backgroundColor: '#1447e6', color: '#ffffff' }}
                    >
                      <LogIn className="w-3.5 h-3.5" />
                      <span>Iniciar Sesión</span>
                    </button>
                  </div>
                ) : reviewCheckData && reviewCheckData.can_review === false && reviewCheckData.reason ? (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded flex items-start gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-black text-xs text-amber-950 font-heading uppercase">
                        No es posible enviar reseña
                      </h4>
                      <p className="text-xs text-amber-800 mt-0.5 leading-snug">
                        {reviewCheckData.reason}
                      </p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmitReview} className="space-y-2.5">
                    <p className="text-[11px] sm:text-xs text-[var(--soft)] leading-snug">
                      ¡Califica tu compra y obtén 10% OFF! en tu próximo pedido.
                    </p>

                    {/* Selector de Estrellas Compacto */}
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-[#06173f] uppercase font-heading">
                        Calificación:
                      </span>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((starVal) => (
                          <button
                            key={starVal}
                            type="button"
                            onClick={() => setReviewRating(starVal)}
                            className="p-0.5 cursor-pointer transition-transform hover:scale-110 focus:outline-none"
                            aria-label={`${starVal} estrellas`}
                          >
                            <Star
                              className={cn(
                                'w-4 h-4 sm:w-5 sm:h-5',
                                starVal <= reviewRating
                                  ? 'text-amber-400 fill-amber-400'
                                  : 'text-slate-200'
                              )}
                            />
                          </button>
                        ))}
                        <span className="ml-1 text-[11px] font-mono font-bold text-slate-600">
                          {reviewRating}/5
                        </span>
                      </div>
                    </div>

                    {/* Comentario */}
                    <div>
                      <textarea
                        value={reviewComment}
                        onChange={(e) => setReviewComment(e.target.value)}
                        rows={2}
                        placeholder="Cuéntanos tu experiencia..."
                        className="w-full text-xs p-2.5 border border-[var(--line)] rounded focus:border-[#1447e6] focus:outline-none resize-none font-sans"
                      />
                    </div>

                    {/* Adjuntar Foto Dropzone */}
                    <div>
                      <label
                        htmlFor="review-file-input"
                        className="group relative flex flex-col items-center justify-center border-2 border-dashed border-[#1447e6] hover:border-[#0a2a9c] rounded-xl p-5 sm:p-6 cursor-pointer bg-[var(--ice)] hover:bg-[#d8effe] transition text-center space-y-1.5"
                      >
                        <input
                          type="file"
                          id="review-file-input"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (file.size > 10 * 1024 * 1024) {
                                setReviewError('La imagen no debe superar los 10 MB.');
                                return;
                              }
                              setReviewError(null);
                              const compressed = await compressImage(file);
                              setReviewFile(compressed);
                              setReviewPreview(URL.createObjectURL(compressed));
                            }
                          }}
                        />

                        {!reviewFile ? (
                          <>
                            <Camera className="w-6 h-6 text-[#1447e6] mx-auto mb-1 stroke-[2.2]" />
                            <div className="text-xs sm:text-sm font-bold text-[#06173f] font-heading">
                              {isWalletRecharge
                                ? 'Adjuntar comprobante de saldo'
                                : 'Subir foto del pedido entregado'}
                            </div>
                            <div className="text-[11px] text-[var(--soft)] font-medium">
                              {isWalletRecharge
                                ? 'Formatos: JPG, PNG o WebP (Máx 10 MB)'
                                : 'Formatos: JPG, PNG o WebP (Obligatorio · Máx 10 MB)'}
                            </div>
                          </>
                        ) : (
                          <div className="flex flex-col items-center space-y-2 w-full">
                            <div className="w-20 h-20 overflow-hidden rounded-lg border-2 border-[#1447e6] bg-white flex items-center justify-center relative shadow-sm">
                              <img
                                src={reviewPreview!}
                                alt="Vista previa"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <span className="text-xs font-bold text-[#06173f] truncate max-w-xs font-heading">
                              {reviewFile.name}
                            </span>
                            <span className="text-[11px] text-[#1447e6] font-bold hover:underline">
                              Toca para cambiar de archivo
                            </span>
                          </div>
                        )}
                      </label>
                    </div>

                    {reviewError && (
                      <p className="text-xs text-red-600 font-bold">{reviewError}</p>
                    )}

                    {/* Botón de Envío */}
                    <button
                      type="submit"
                      id="btn-submit-review"
                      disabled={!isReviewValid || isSubmittingReview}
                      className={cn(
                        "btn w-full font-bold transition flex items-center justify-center gap-1.5 py-2.5 text-xs sm:text-sm font-heading",
                        isReviewValid && !isSubmittingReview
                          ? "cursor-pointer text-white"
                          : "cursor-not-allowed opacity-90 text-white/90"
                      )}
                      style={{
                        backgroundColor: isReviewValid && !isSubmittingReview ? '#1447e6' : '#94a3b8',
                        color: '#ffffff',
                      }}
                    >
                      {isSubmittingReview ? (
                        <RotateCw className="w-3.5 h-3.5 animate-spin mr-1" />
                      ) : (
                        <Send className="w-3.5 h-3.5 mr-1 stroke-[2.5]" />
                      )}
                      <span>{isSubmittingReview ? 'Enviando...' : 'Enviar'}</span>
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* TARJETA 1: CÓDIGO DE PEDIDO (Imágenes 1 y 3) */}
            <div
              className="pn bg-white border border-[var(--line)] p-6 sm:p-7 space-y-3.5 shadow-sm"
              style={{
                clipPath: 'polygon(14px 0, 100% 0, 100% 100%, 0 100%, 0 14px)',
                filter: 'drop-shadow(4px 4px 0 rgba(6, 23, 63, 0.04))',
              }}
            >
              <span className="text-[11px] font-bold text-[var(--soft)] uppercase tracking-wider font-heading block">
                CÓDIGO DE PEDIDO
              </span>

              <div className="flex items-center justify-between gap-3">
                <h1 className="text-2xl sm:text-3xl font-black tracking-wide text-[#06173f] font-heading">
                  {order.order_code.startsWith('#')
                    ? order.order_code.substring(1)
                    : order.order_code}
                </h1>
                <button
                  type="button"
                  onClick={() => copyToClipboard(order.order_code, 'Código de pedido')}
                  className="w-9 h-9 border border-[var(--line)] bg-white hover:bg-[#eff6ff] flex items-center justify-center text-[#1447e6] transition cursor-pointer"
                  style={{
                    clipPath:
                      'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
                  }}
                  title="Copiar código"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>

              {/* Status Badge */}
              <div className="pt-0.5">{renderStatusBadge(order.status)}</div>

              {/* Clean 1px divider */}
              <div className="border-b border-[var(--line)] pt-1" />

              {/* Grilla 2x2 limpia SIN cajas dentro de cajas (Imágenes 1 y 3) */}
              <div className="grid grid-cols-2 gap-y-4 gap-x-6 text-xs pt-1">
                <div>
                  <span className="text-[10px] sm:text-[11px] font-bold text-[var(--soft)] uppercase tracking-wider block font-heading">
                    CLIENTE FORTNITE
                  </span>
                  <span className="text-sm sm:text-base font-bold text-[#06173f] block mt-0.5 font-heading truncate">
                    {order.customer_username}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] sm:text-[11px] font-bold text-[var(--soft)] uppercase tracking-wider block font-heading">
                    TOTAL PAVOS
                  </span>
                  <span className="text-sm sm:text-base font-bold text-[#06173f] block mt-0.5 font-heading">
                    {order.total_vbucks.toLocaleString('es-AR')} Pavos
                  </span>
                </div>
                <div>
                  <span className="text-[10px] sm:text-[11px] font-bold text-[var(--soft)] uppercase tracking-wider block font-heading">
                    MONTO A ABONAR
                  </span>
                  <span className="text-sm sm:text-base font-black text-[#1447e6] block mt-0.5 font-heading">
                    {formattedPriceString}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] sm:text-[11px] font-bold text-[var(--soft)] uppercase tracking-wider block font-heading">
                    MÉTODO
                  </span>
                  <span className="text-sm sm:text-base font-bold text-[#06173f] block mt-0.5 font-heading">
                    {order.payment_method === 'SALDO_BILLETERA'
                      ? 'Saldo de Cuenta'
                      : 'Transferencia Bancaria'}
                  </span>
                </div>
              </div>
            </div>

            {/* TARJETA 2: TEMPORIZADOR REGRESIVO 60 MIN (Imagen 1) */}
            {/* Solo se muestra si el pedido está PENDIENTE_PAGO. Oculto si CANCELADO (Imagen 3) */}
            {order.status === 'PENDIENTE_PAGO' && (
              <div
                className="pn bg-white border border-[var(--line)] p-6 sm:p-7 text-center space-y-2 shadow-sm"
                style={{
                  clipPath: 'polygon(14px 0, 100% 0, 100% 100%, 0 100%, 0 14px)',
                  filter: 'drop-shadow(4px 4px 0 rgba(6, 23, 63, 0.04))',
                }}
              >
                <div className="flex items-center justify-center gap-2 text-xs font-bold text-[#06173f] uppercase tracking-wider font-heading">
                  <Timer className="w-4 h-4 text-[#1447e6] shrink-0" />
                  <span>TIEMPO RESTANTE PARA REALIZAR Y CONFIRMAR EL PAGO</span>
                </div>
                <div className="text-4xl sm:text-5xl font-black tracking-wider text-[#1447e6] font-heading my-1">
                  {formattedTimer}
                </div>
                <p className="text-xs text-[var(--soft)] max-w-sm mx-auto font-medium leading-relaxed">
                  Los precios se reservan por un máximo de 60 minutos desde la emisión del pedido.
                </p>
              </div>
            )}

            {/* TARJETA 3: DATOS DE TRANSFERENCIA (Imágenes 2 y 3) */}
            {order.payment_method === 'TRANSFERENCIA_BANCARIA' && (
              <div
                className="pn bg-white border border-[var(--line)] p-6 sm:p-7 space-y-3 shadow-sm"
                style={{
                  clipPath: 'polygon(14px 0, 100% 0, 100% 100%, 0 100%, 0 14px)',
                  filter: 'drop-shadow(4px 4px 0 rgba(6, 23, 63, 0.04))',
                }}
              >
                <div
                  className={cn(
                    'flex items-center justify-between pb-1',
                    order.status !== 'PENDIENTE_PAGO' && 'cursor-pointer select-none'
                  )}
                  onClick={() => {
                    if (order.status !== 'PENDIENTE_PAGO') {
                      setIsTransferOpen((prev) => !prev);
                    }
                  }}
                >
                  <div className="flex items-center gap-2.5">
                    <Building2 className="w-5 h-5 text-[#1447e6]" />
                    <h3 className="font-bold text-base text-[#06173f] font-heading">
                      Datos de transferencia
                    </h3>
                  </div>

                  {order.status !== 'PENDIENTE_PAGO' && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsTransferOpen((prev) => !prev);
                      }}
                      className="p-1 text-slate-500 hover:text-[#1447e6] transition-colors cursor-pointer bg-transparent border-0 outline-none"
                      aria-label={isTransferOpen ? 'Contraer datos de transferencia' : 'Expandir datos de transferencia'}
                    >
                      <ChevronDown
                        className={cn(
                          'w-5 h-5 transition-transform duration-200',
                          isTransferOpen ? 'rotate-180' : 'rotate-0'
                        )}
                      />
                    </button>
                  )}
                </div>

                {(order.status === 'PENDIENTE_PAGO' || isTransferOpen) && (
                  <>
                    <div className="border-b border-[var(--line)]" />

                    {/* Lista de filas limpias con divisores de 1px (Imágenes 2 y 3) */}
                <div className="divide-y divide-[var(--line)] text-xs">
                  {/* Fila Monto Exacto */}
                  <div className="flex justify-between items-center py-3">
                    <div>
                      <span className="text-[10px] font-bold text-[var(--soft)] uppercase tracking-wider font-heading block">
                        MONTO EXACTO
                      </span>
                      <span className="text-sm sm:text-base font-black text-[#1447e6] font-heading block mt-0.5">
                        {formattedPriceString}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(formattedPriceString, 'Monto exacto')}
                      className="flex items-center gap-1.5 text-xs font-bold text-[#1447e6] hover:bg-blue-50 px-2 py-1 rounded transition cursor-pointer font-heading"
                    >
                      <Copy className="w-3.5 h-3.5 text-[#1447e6]" />
                      <span>Copiar</span>
                    </button>
                  </div>

                  {/* Fila Alias o CLABE */}
                  <div className="flex justify-between items-center py-3">
                    <div className="min-w-0 flex-1 pr-2">
                      <span className="text-[10px] font-bold text-[var(--soft)] uppercase tracking-wider font-heading block">
                        {order.currency === 'MXN' ? 'CLABE INTERBANCARIA' : 'ALIAS'}
                      </span>
                      <span className="text-sm font-bold text-[#06173f] truncate block font-heading mt-0.5">
                        {order.currency === 'MXN'
                          ? bankDetails.clabe || '734180020023721778'
                          : bankDetails.alias || 'ltirimacco.mp'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        copyToClipboard(
                          order.currency === 'MXN'
                            ? bankDetails.clabe || '734180020023721778'
                            : bankDetails.alias || 'ltirimacco.mp',
                          'Alias / Clabe'
                        )
                      }
                      className="flex items-center gap-1.5 text-xs font-bold text-[#1447e6] hover:bg-blue-50 px-2 py-1 rounded transition cursor-pointer font-heading shrink-0"
                    >
                      <Copy className="w-3.5 h-3.5 text-[#1447e6]" />
                      <span>Copiar</span>
                    </button>
                  </div>

                  {/* Fila Beneficiario / Titular */}
                  <div className="flex justify-between items-center py-3">
                    <div>
                      <span className="text-[10px] font-bold text-[var(--soft)] uppercase tracking-wider font-heading block">
                        BENEFICIARIO / TITULAR
                      </span>
                      <span className="text-sm font-bold text-[#06173f] block font-heading mt-0.5">
                        {bankDetails.holder || 'Lucas Tirimacco'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        copyToClipboard(bankDetails.holder || 'Lucas Tirimacco', 'Titular')
                      }
                      className="flex items-center gap-1.5 text-xs font-bold text-[#1447e6] hover:bg-blue-50 px-2 py-1 rounded transition cursor-pointer font-heading"
                    >
                      <Copy className="w-3.5 h-3.5 text-[#1447e6]" />
                      <span>Copiar</span>
                    </button>
                  </div>

                  {/* Fila Entidad Bancaria */}
                  <div className="flex justify-between items-center py-3">
                    <div>
                      <span className="text-[10px] font-bold text-[var(--soft)] uppercase tracking-wider font-heading block">
                        ENTIDAD BANCARIA
                      </span>
                      <span className="text-sm font-bold text-[#06173f] block font-heading mt-0.5">
                        {bankDetails.bank || (order.currency === 'MXN' ? 'Finco Pay' : 'Mercado Pago')}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        copyToClipboard(
                          bankDetails.bank || (order.currency === 'MXN' ? 'Finco Pay' : 'Mercado Pago'),
                          'Banco'
                        )
                      }
                      className="flex items-center gap-1.5 text-xs font-bold text-[#1447e6] hover:bg-blue-50 px-2 py-1 rounded transition cursor-pointer font-heading"
                    >
                      <Copy className="w-3.5 h-3.5 text-[#1447e6]" />
                      <span>Copiar</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

            {/* TARJETA 4: COSMÉTICOS / RECARGA EN ESTE PEDIDO (Imágenes 2 y 3) */}
            <div
              className="pn bg-white border border-[var(--line)] p-6 sm:p-7 space-y-3 shadow-sm"
              style={{
                clipPath: 'polygon(14px 0, 100% 0, 100% 100%, 0 100%, 0 14px)',
                filter: 'drop-shadow(4px 4px 0 rgba(6, 23, 63, 0.04))',
              }}
            >
              <h4 className="text-[11px] font-bold uppercase tracking-wider text-[var(--soft)] font-heading pb-1">
                {orderItemsTitle}
              </h4>

              <div className="border-b border-[var(--line)]" />

              <div className="divide-y divide-[var(--line)] text-xs">
                {order.items && order.items.length > 0 ? (
                  order.items.map((it, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-2.5 gap-2"
                    >
                      <span className="font-bold text-[#06173f] truncate max-w-[280px] sm:max-w-md font-heading">
                        {it.name || (isWalletRecharge ? 'Recarga de Saldo' : isDirectRecharge ? 'Recarga' : 'Cosmético')}
                      </span>
                      <span className="text-[#1447e6] font-heading font-bold shrink-0">
                        {(it.vbucks || 0).toLocaleString('es-AR')} Pavos
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="text-xs text-[var(--soft)] py-2">
                    {typeof order.cart_summary === 'string'
                      ? order.cart_summary
                      : order.cart_summary && typeof order.cart_summary === 'object' && 'vbucks' in order.cart_summary
                      ? `${(order.cart_summary as any).vbucks} Pavos`
                      : isWalletRecharge
                      ? 'Recarga de Saldo'
                      : isDirectRecharge
                      ? 'Recargas'
                      : 'Cosméticos varios'}
                  </div>
                )}
              </div>
            </div>

            {/* TARJETA 5: COMPROBANTE DE PAGO (Imágenes 2 y 3) */}
            {order.payment_method === 'TRANSFERENCIA_BANCARIA' && (
              <div
                className="pn bg-white border border-[var(--line)] p-6 sm:p-7 space-y-4 shadow-sm"
                style={{
                  clipPath: 'polygon(14px 0, 100% 0, 100% 100%, 0 100%, 0 14px)',
                  filter: 'drop-shadow(4px 4px 0 rgba(6, 23, 63, 0.04))',
                }}
              >
                <div className="flex items-center gap-2.5 pb-1">
                  <Receipt className="w-5 h-5 text-[#1447e6]" />
                  <h3 className="font-bold text-base text-[#06173f] font-heading">
                    Comprobante de pago
                  </h3>
                </div>

                <div className="border-b border-[var(--line)]" />

                {/* Estado: Comprobante Ya Subido */}
                {(order.status === 'PAGO_INFORMADO' || order.status === 'COMPLETADO') && (
                  <div className="p-5 space-y-3 text-center bg-[#ecfdf5] border border-[#a7f3d0] rounded">
                    <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xl mx-auto">
                      <Check className="w-6 h-6 stroke-[3]" />
                    </div>
                    <h4 className="font-bold text-sm text-emerald-800 font-heading uppercase">
                      ¡Comprobante registrado!
                    </h4>
                    <p className="text-xs text-emerald-900 leading-relaxed max-w-md mx-auto">
                      {isWalletRecharge
                        ? 'Lucas verificará la acreditación en la cuenta bancaria y acreditará tu saldo de inmediato.'
                        : isDirectRecharge
                        ? 'Lucas verificará la acreditación en la cuenta bancaria y gestionará la carga a tu cuenta de inmediato.'
                        : 'Lucas verificará la acreditación en la cuenta bancaria y autorizará la entrega de tus regalos de inmediato.'}
                    </p>
                    {order.receipt_url && (
                      <div className="pt-2">
                        <a
                          href={order.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn lt sm cursor-pointer inline-flex items-center gap-1.5"
                        >
                          <ImageIcon className="w-4 h-4 text-[#1447e6]" />
                          <span>Ver Comprobante Subido</span>
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {/* Estado: Pedido Cancelado */}
                {order.status === 'CANCELADO' && (
                  <div className="p-5 bg-[#fef2f2] border border-[#fecaca] rounded space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                        <Ban className="w-4 h-4 text-red-600" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="font-heading font-black text-xs uppercase tracking-wide text-red-900">
                          Pedido Cancelado
                        </h4>
                        <p className="text-xs text-red-800 leading-relaxed font-medium">
                          {(order as any).cancelled_by === 'USER'
                            ? 'Pedido cancelado por el usuario.'
                            : isExpiredByTimer
                            ? 'El tiempo para realizar el pago ha expirado. Si ya realizaste el pago vuelve a crear un nuevo pedido y sube el comprobante o contáctanos por WhatsApp.'
                            : 'Este pedido ha expirado o fue cancelado. Si ya realizaste el pago vuelve a crear un nuevo pedido y sube el comprobante o contáctanos por WhatsApp.'}
                        </p>
                      </div>
                    </div>
                    {(order as any).cancelled_by !== 'USER' && (
                      <div className="pt-1 flex flex-wrap gap-2">
                        <a
                          href={`https://wa.me/5492353421028?text=${encodeURIComponent(
                            `¡Hola! Mi pedido #${order.order_code} expiró o fue cancelado pero ya realicé el pago. Mi usuario de Fortnite es ${order.customer_username}. Adjunto comprobante para validarlo.`
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#20ba59] text-white font-heading font-bold text-xs uppercase tracking-wider rounded transition no-underline shadow-sm"
                        >
                          <i className="fa-brands fa-whatsapp text-sm" />
                          <span>Contactar por WhatsApp</span>
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {/* Formulario de Carga: Pendiente de Pago */}
                {order.status === 'PENDIENTE_PAGO' && (
                  <div className="space-y-4">
                    <p className="text-xs text-[var(--soft)] leading-relaxed font-medium">
                      Realiza la transferencia desde tu cuenta y adjunta aquí el comprobante.
                    </p>

                    {/* Dropzone Comprobante */}
                    <label
                      htmlFor="receipt-file-input"
                      className="group relative flex flex-col items-center justify-center border-2 border-dashed border-[#1447e6] hover:border-[#0a2a9c] rounded-xl p-5 sm:p-6 cursor-pointer bg-[var(--ice)] hover:bg-[#d8effe] transition text-center space-y-1.5"
                    >
                      <input
                        type="file"
                        id="receipt-file-input"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        className="hidden"
                        onChange={handleFileChange}
                      />

                      {!selectedFile ? (
                        <>
                          <CloudUpload className="w-6 h-6 text-[#1447e6] mx-auto mb-1 stroke-[2.2]" />
                          <div className="text-xs sm:text-sm font-bold text-[#06173f] font-heading">
                            Toca o arrastra aquí tu comprobante
                          </div>
                          <div className="text-[11px] text-[var(--soft)] font-medium">
                            Formatos: JPG, PNG, WEBP o PDF (Máx 10 MB)
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center space-y-2 w-full">
                          <div className="w-20 h-20 overflow-hidden rounded-lg border-2 border-[#1447e6] bg-white flex items-center justify-center relative shadow-sm">
                            {filePreview ? (
                              <img
                                src={filePreview}
                                alt="Vista previa"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <FileText className="w-10 h-10 text-red-500" />
                            )}
                          </div>
                          <span className="text-xs font-bold text-[#06173f] truncate max-w-xs font-heading">
                            {selectedFile.name}
                          </span>
                          <span className="text-[11px] text-[#1447e6] font-bold hover:underline">
                            Toca para cambiar de archivo
                          </span>
                        </div>
                      )}
                    </label>

                    {/* Botón Confirmar Pago */}
                    <button
                      type="button"
                      id="btn-confirm-payment"
                      onClick={handleUploadReceipt}
                      disabled={!selectedFile || isUploading}
                      className="btn w-full font-bold cursor-pointer transition flex items-center justify-center gap-2"
                      style={{
                        width: '100%',
                        fontSize: '15px',
                        backgroundColor: selectedFile ? '#1447e6' : '#94a3b8',
                        color: '#ffffff',
                      }}
                    >
                      {isUploading ? (
                        <RotateCw className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Check className="w-4 h-4 mr-1 stroke-[3]" />
                      )}
                      <span>{isUploading ? 'Subiendo comprobante...' : 'Confirmar pago'}</span>
                    </button>

                    {/* Botón Cancelar Pedido */}
                    <div className="pt-2 border-t border-[var(--line)]">
                      <button
                        type="button"
                        id="btn-cancel-order"
                        onClick={() => setShowCancelConfirm(true)}
                        className="btn danger w-full sm cursor-pointer flex items-center justify-center gap-2"
                        style={{
                          width: '100%',
                          fontSize: '14px',
                          background: '#dc2626',
                          color: '#ffffff',
                        }}
                      >
                        <Ban className="w-4 h-4 mr-1" />
                        <span>Cancelar pedido</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* TARJETA DESTACADA COORDINACIÓN DE RECARGA POR WHATSAPP */}
            {(() => {
              const hasRechargeItems = (order.items || []).some((it) => {
                const oid = it.offer_id || it.offerId || '';
                return (
                  oid === 'recharge_vbucks_12500' ||
                  oid === 'recharge_crew' ||
                  oid.startsWith('recharge_') ||
                  (it as any).category === 'Recargas'
                );
              });
              const isPaidOrReceiptUploaded =
                order.status === 'PAGO_INFORMADO' ||
                order.status === 'COMPLETADO' ||
                order.status === 'APROBADO_ENVIANDO';

              if (!hasRechargeItems || !isPaidOrReceiptUploaded) return null;

              const rechargeItemsList = (order.items || [])
                .filter((it) => {
                  const oid = it.offer_id || it.offerId || '';
                  return (
                    oid === 'recharge_vbucks_12500' ||
                    oid === 'recharge_crew' ||
                    oid.startsWith('recharge_') ||
                    (it as any).category === 'Recargas'
                  );
                })
                .map((i) => i.name)
                .filter(Boolean)
                .join(', ') || 'Recargas';

              return (
                <div
                  className="pn bg-[#f0fdf4] border-2 border-[#22c55e] p-6 space-y-4 shadow-sm"
                  style={{
                    clipPath: 'polygon(14px 0, 100% 0, 100% 100%, 0 100%, 0 14px)',
                    filter: 'drop-shadow(4px 4px 0 #bbf7d0)',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 bg-emerald-500 text-white flex items-center justify-center text-2xl shrink-0"
                      style={{ clipPath: 'var(--cut)' }}
                    >
                      <i className="fa-brands fa-whatsapp" />
                    </div>
                    <div>
                      <h3 className="text-base font-black uppercase text-emerald-950 font-heading">
                        Coordinar Carga por WhatsApp
                      </h3>
                      <p className="text-xs text-emerald-800">
                        Tu pedido incluye recargas a tu cuenta de Fortnite. Escríbenos por WhatsApp para coordinar la carga inmediata.
                      </p>
                    </div>
                  </div>
                  <div className="pt-1">
                    <a
                      href={`https://wa.me/5492353421028?text=${encodeURIComponent(
                        `¡Hola! Ya realicé el pago de mi pedido #${order.order_code} de ${rechargeItemsList}. Mi usuario de Fortnite es ${order.customer_username}. Vengo para coordinar la carga a mi cuenta.`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-3.5 px-4 bg-[#25D366] hover:bg-[#20ba59] text-white font-heading font-black text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition shadow-md cursor-pointer no-underline"
                      style={{ clipPath: 'var(--slash)' }}
                    >
                      <i className="fa-brands fa-whatsapp text-2xl" />
                      <span>Escribir por WhatsApp para cargar pedido</span>
                    </a>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </main>

      {/* Confirmation Modal for Order Cancellation */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="pn max-w-sm w-full bg-white border border-[var(--line)] p-6 space-y-4 shadow-2xl">
            <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto">
              <Ban className="w-6 h-6 stroke-[2.5]" />
            </div>
            <div className="text-center space-y-1">
              <h3 className="font-heading font-black text-lg text-[#06173f]">
                ¿Cancelar pedido?
              </h3>
              <p className="text-xs text-[var(--soft)] leading-relaxed">
                ¿Estás seguro de que deseas cancelar este pedido? Si no realizaste el pago, se anulará la orden de inmediato.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowCancelConfirm(false)}
                className="btn lt sm cursor-pointer"
              >
                Volver
              </button>
              <button
                type="button"
                onClick={handleCancelOrder}
                disabled={isCancelling}
                className="btn danger sm cursor-pointer bg-red-600 text-white"
                style={{ background: '#dc2626' }}
              >
                {isCancelling ? 'Cancelando...' : 'Sí, cancelar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderPage;
